import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Badge from '~/components/Badge'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import TagInput from '~/components/TagInput'

const TagInputPage: Component = () => {
  const [techStack, setTechStack] = createSignal(['SolidJS', 'Tailwind CSS', 'TypeScript'])
  const [categories, setCategories] = createSignal<string[]>([])

  const availableTech = ['React', 'Vue', 'Angular', 'Node.js', 'Vite', 'Bun', 'Rust', 'Go', 'Docker', 'AWS']

  return (
    <>
      <Title>TagInput - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">TagInput</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Categorización dinámica de datos. El componente TagInput permite la captura y gestión de arreglos de cadenas mediante una interfaz intuitiva con autocompletado, validación y soporte para
            múltiples variantes visuales.
          </p>
        </div>

        <div class="space-y-12">
          {/* Casos de Uso: Selección vs Entrada */}
          <Card>
            <Card.Header
              title="Modos de Captura"
              subtitle="Selección desde diccionario predefinido o entrada libre de datos."
              icon={<Icon icon="material-symbols-light:sell-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 p-10 bg-surface-muted rounded-3xl border border-border border-dashed">
                <div class="space-y-6">
                  <Badge variant="soft" color="info" size="sm">
                    Mode: Select (Dict Search)
                  </Badge>
                  <TagInput label="Stack Tecnológico" tags={techStack()} onTagsChange={setTechStack} mode="select" availableTags={availableTech} placeholder="Busca tecnologías..." color="primary" />
                  <div class="p-4 bg-surface rounded-2xl border border-border shadow-sm">
                    <span class="text-[10px] font-bold text-foreground-subtle uppercase tracking-widest block mb-2">Internal State</span>
                    <div class="flex flex-wrap gap-2 text-xs font-mono text-primary-600 italic">
                      [{' '}
                      {techStack()
                        .map(t => `"${t}"`)
                        .join(', ')}{' '}
                      ]
                    </div>
                  </div>
                </div>
                <div class="space-y-6">
                  <Badge variant="soft" color="secondary" size="sm">
                    Mode: Input (Raw Tags)
                  </Badge>
                  <TagInput label="Palabras Clave (SEO)" tags={categories()} onTagsChange={setCategories} mode="input" placeholder="Escribe y presiona Enter..." color="secondary" />
                  <div class="flex items-center gap-3 p-4 bg-warning-soft rounded-2xl border border-warning/25">
                    <Icon icon="material-symbols-light:info-outline" class="text-amber-500 mt-0.5" size={20} />
                    <p class="text-xs text-on-warning-soft leading-relaxed italic">
                      El modo <b>Input</b> permite la creación de etiquetas on-the-fly sin necesidad de un diccionario previo.
                    </p>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Matrix de Personalización */}
          <Card>
            <Card.Header
              title="Variaciones Estéticas"
              subtitle="Control total sobre el tamaño, color y densidad de las etiquetas."
              icon={<Icon icon="material-symbols-light:palette-outline" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div class="p-6 bg-surface-muted rounded-2xl space-y-4">
                  <div class="text-xs font-bold text-foreground-subtle uppercase tracking-widest">Densidad Compacta</div>
                  <TagInput size="sm" tags={['XS', 'SM']} mode="input" color="success" />
                  <p class="text-[10px] text-foreground-subtle mt-2 italic line-clamp-2">Ideal para sidebars o áreas con espacio restringido.</p>
                </div>
                <div class="p-6 bg-surface-muted rounded-2xl space-y-4">
                  <div class="text-xs font-bold text-foreground-subtle uppercase tracking-widest">Enfoque Primario</div>
                  <TagInput tags={['System', 'Kernel']} mode="input" color="primary" />
                  <p class="text-[10px] text-foreground-subtle mt-2 italic line-clamp-2">Variante estándar para formularios de configuración global.</p>
                </div>
                <div class="p-6 bg-surface-muted rounded-2xl space-y-4">
                  <div class="text-xs font-bold text-foreground-subtle uppercase tracking-widest">Validación Errónea</div>
                  <TagInput tags={['Bad Data']} mode="input" error="Se requieren al menos 3 etiquetas." />
                  <p class="text-[10px] text-foreground-subtle mt-2 italic line-clamp-2">Manejo de estados de error integrados con el sistema de formularios.</p>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Tamaños */}
          <Card>
            <Card.Header
              title="Tamaños"
              subtitle="Cinco densidades alineadas con Select y Button: xs, sm, md, lg y xl."
              icon={<Icon icon="material-symbols-light:straighten" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-4 p-10 bg-surface-muted rounded-2xl border border-border">
                <TagInput size="xs" tags={['xs']} mode="input" color="primary" />
                <TagInput size="sm" tags={['sm']} mode="input" color="primary" />
                <TagInput size="md" tags={['md']} mode="input" color="primary" />
                <TagInput size="lg" tags={['lg']} mode="input" color="primary" />
                <TagInput size="xl" tags={['xl']} mode="input" color="primary" />
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Especificaciones técnicas para el componente TagInput."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">tags</td>
                      <td class="px-6 py-4 italic text-xs">string[]</td>
                      <td class="px-6 py-4 font-mono">[]</td>
                      <td class="px-6 py-4 italic">Arreglo de etiquetas activas.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">size</td>
                      <td class="px-6 py-4 italic text-xs">'xs' | 'sm' | 'md' | 'lg' | 'xl'</td>
                      <td class="px-6 py-4 font-mono">'md'</td>
                      <td class="px-6 py-4 italic">Densidad del contenedor y tamaño de los chips.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">mode</td>
                      <td class="px-6 py-4 italic text-xs">'select' | 'input'</td>
                      <td class="px-6 py-4 font-mono">'input'</td>
                      <td class="px-6 py-4">Define el origen de los datos de las etiquetas.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-secondary-600">availableTags</td>
                      <td class="px-6 py-4 italic text-xs">string[]</td>
                      <td class="px-6 py-4 font-mono">[]</td>
                      <td class="px-6 py-4 italic text-balance">Opciones disponibles para el modo 'select'.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">maxTags</td>
                      <td class="px-6 py-4 italic text-xs">number</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4">Límite máximo de elementos permitidos.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default TagInputPage
